/**
 * Sync Queue Recovery Page (/sync-queue)
 *
 * Diagnostic + recovery tool for stuck offline-queue items.
 *
 * What it does:
 *  • Lists every pending and failed queue item with: type, age, error, retry count
 *  • Lists every local Field Notes draft (unsynced or otherwise)
 *  • Per-item actions: Retry, Discard
 *  • Bulk actions: Retry all failed, Discard all failed, Sync now, Export JSON
 *  • Export full diagnostic JSON for support tickets (no base64 blobs included)
 *
 * Built for contractors stuck in the field with a poisoned queue. Open this on
 * the same device that has the stuck items.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, RefreshCw, Trash2, Download, AlertTriangle, CheckCircle2,
  Clock, FileText, Image as ImageIcon, Wifi, WifiOff,
} from 'lucide-react';
import {
  getAllActions, getFailedActions, retryFailedAction, discardAction,
  resetFailedActions, clearFailedActions, clearAllActions,
  syncOfflineActions, dumpQueueDiagnostics,
  queueOfflineAction, OFFLINE_ACTIONS,
} from '@/lib/offlineQueue';
import {
  getAllPhotoUploads, retryPhotoUpload, discardPhotoUpload, processPhotoUploads,
} from '@/lib/photoUploadManager';
import { getRecentLogs } from '@/lib/syncLogger';
import { getAll, remove, STORES } from '@/lib/offlineDB';
import { useOffline } from '@/context/OfflineContext';
import { useAuth } from '@/context/AuthContext';
import { useDeveloperMode } from '@/hooks/useDeveloperMode';

const errorClassLabels = {
  auth: { label: 'Login required', color: 'bg-red-100 text-red-700' },
  validation: { label: 'Bad data', color: 'bg-orange-100 text-orange-700' },
  network: { label: 'Network', color: 'bg-amber-100 text-amber-700' },
  server: { label: 'Server', color: 'bg-amber-100 text-amber-700' },
  unknown: { label: 'Unknown', color: 'bg-gray-100 text-gray-700' },
};

const actionTypeLabels = {
  UPDATE_SITE_VISIT_FIELD_NOTES: 'Field notes update',
  CREATE_CLIENT: 'New client',
  UPDATE_CLIENT: 'Client update',
  CREATE_ESTIMATE: 'New estimate',
  UPDATE_ESTIMATE: 'Estimate update',
  UPDATE_JOB_STATUS: 'Job status change',
  ADD_JOB_NOTES: 'Job notes',
  ADD_JOB_PHOTOS: 'Job photos',
  ADD_ESTIMATE_NOTES: 'Estimate notes',
};

function relativeTime(iso) {
  if (!iso) return '—';
  const d = Date.now() - new Date(iso).getTime();
  if (d < 60_000) return `${Math.round(d / 1000)}s ago`;
  if (d < 3_600_000) return `${Math.round(d / 60_000)}m ago`;
  if (d < 86_400_000) return `${Math.round(d / 3_600_000)}h ago`;
  return `${Math.round(d / 86_400_000)}d ago`;
}

export default function SyncQueueRecoveryPage() {
  const { isOnline, refreshPendingCount, syncPendingActions } = useOffline();
  const { user, organization } = useAuth();
  const developerMode = useDeveloperMode();

  // All hooks must be declared BEFORE any early return to satisfy
  // react-hooks/rules-of-hooks.
  const [actions, setActions] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showLogs, setShowLogs] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [all, allDrafts, allPhotos, recentLogs] = await Promise.all([
        getAllActions(),
        getAll(STORES.FIELD_NOTES_DRAFTS),
        getAllPhotoUploads(),
        getRecentLogs(50),
      ]);
      setActions(all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setDrafts(allDrafts.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)));
      setPhotos(allPhotos.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setLogs(recentLogs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Platform-admin-gated route. Hidden by default; toggled per-org from
  // /admin-dash → Organization → Feature Toggles → "Developer Mode".
  // We render a soft "not found" instead of redirecting so deep links from
  // support sessions are obvious when the flag has been left off.
  if (organization && !developerMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="sync-queue-locked">
        <div className="bg-white rounded-2xl border border-gray-100 p-8 max-w-md text-center shadow-sm">
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
            <FileText className="w-5 h-5 text-gray-400" />
          </div>
          <p className="font-semibold text-gray-900">Page not available</p>
          <p className="text-sm text-gray-500 mt-1">
            The sync queue diagnostic is hidden by default. If you need it for a
            support session, contact us and we'll enable it temporarily.
          </p>
          <Link
            to="/dashboard"
            className="inline-block mt-4 text-sm font-medium text-blue-600 hover:text-blue-700"
            data-testid="back-to-dashboard"
          >
            ← Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  const pending = actions.filter(a => a.status === 'pending');
  const failed = actions.filter(a => a.status === 'failed');
  const pendingPhotos = photos.filter(p => p.status === 'pending');
  const failedPhotos = photos.filter(p => p.status === 'failed');

  const handleSyncAll = async () => {
    if (!isOnline) {
      toast.error('You are offline. Reconnect first.');
      return;
    }
    setBusy(true);
    try {
      // Drain photos first, then actions
      await processPhotoUploads();
      await syncPendingActions();
      await load();
      await refreshPendingCount();
    } finally {
      setBusy(false);
    }
  };

  const handleRetryPhoto = async (id) => {
    setBusy(true);
    try {
      await retryPhotoUpload(id);
      if (isOnline) await processPhotoUploads();
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleDiscardPhoto = async (id) => {
    if (!window.confirm('Discard this photo? The image will be permanently deleted from this device.')) return;
    setBusy(true);
    try {
      await discardPhotoUpload(id);
      toast.success('Photo discarded');
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleRetryAllFailed = async () => {
    setBusy(true);
    try {
      const n = await resetFailedActions();
      toast.success(`Reset ${n} failed item${n === 1 ? '' : 's'} to pending`);
      await load();
      if (isOnline) {
        await syncPendingActions();
        await load();
      }
      await refreshPendingCount();
    } finally {
      setBusy(false);
    }
  };

  const handleClearFailed = async () => {
    if (!window.confirm(`Permanently discard ${failed.length} failed item${failed.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const n = await clearFailedActions();
      toast.success(`Discarded ${n} item${n === 1 ? '' : 's'}`);
      await load();
      await refreshPendingCount();
    } finally {
      setBusy(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm(`Permanently discard ALL ${actions.length} queued items? Field notes drafts will be kept. This cannot be undone.`)) return;
    setBusy(true);
    try {
      const n = await clearAllActions();
      toast.success(`Discarded ${n} item${n === 1 ? '' : 's'}`);
      await load();
      await refreshPendingCount();
    } finally {
      setBusy(false);
    }
  };

  const handleRetryOne = async (id) => {
    setBusy(true);
    try {
      await retryFailedAction(id);
      if (isOnline) await syncPendingActions();
      await load();
      await refreshPendingCount();
    } finally {
      setBusy(false);
    }
  };

  const handleDiscardOne = async (id) => {
    if (!window.confirm('Discard this item?')) return;
    setBusy(true);
    try {
      await discardAction(id);
      toast.success('Discarded');
      await load();
      await refreshPendingCount();
    } finally {
      setBusy(false);
    }
  };

  const handleDiscardDraft = async (draftId) => {
    if (!window.confirm('Discard this draft? Any unsynced notes will be lost.')) return;
    await remove(STORES.FIELD_NOTES_DRAFTS, draftId);
    toast.success('Draft discarded');
    await load();
  };

  /**
   * Promote a local draft into the sync queue. Once queued, the standard sync
   * loop will push it to the server (with photo upload isolation + backoff
   * retries). If we're online we also kick off a sync immediately and, on
   * success, remove the draft so it doesn't reappear next session.
   *
   * Used for drafts that pre-date the offline-save fix — these never made it
   * into the queue because handleSave used to call the network directly.
   */
  const handlePushDraftToQueue = async (draft) => {
    setBusy(true);
    try {
      await queueOfflineAction(
        OFFLINE_ACTIONS.UPDATE_SITE_VISIT_FIELD_NOTES,
        {
          site_visit_id: draft.site_visit_id,
          field_notes: draftToFieldNotesPayload(draft),
          user_id: user?.id,
          user_name: user?.name,
        },
        draft.org_id || organization?.id,
      );
      if (isOnline) {
        // Drain immediately for instant gratification
        const result = await syncOfflineActions();
        if (result?.failed > 0) {
          toast.warning('Queued, but server rejected the save. Check the failed items.');
        } else if (result?.success > 0) {
          // Successfully synced — safe to remove the local draft
          try { await remove(STORES.FIELD_NOTES_DRAFTS, draft.id); } catch { /* ignore */ }
          toast.success('Draft synced to server');
        } else {
          toast.success('Draft queued — will sync shortly');
        }
      } else {
        toast.success('Draft queued — will sync when you reconnect');
      }
      await load();
      await refreshPendingCount();
    } catch (e) {
      console.error('[SyncQueue] Failed to push draft:', e);
      toast.error('Could not queue draft. Try again or export the diagnostic.');
    } finally {
      setBusy(false);
    }
  };

  const handlePushAllDrafts = async () => {
    if (!drafts.length) return;
    if (!window.confirm(`Push all ${drafts.length} draft${drafts.length === 1 ? '' : 's'} into the sync queue?`)) return;
    setBusy(true);
    try {
      for (const d of drafts) {
        try {
          await queueOfflineAction(
            OFFLINE_ACTIONS.UPDATE_SITE_VISIT_FIELD_NOTES,
            {
              site_visit_id: d.site_visit_id,
              field_notes: draftToFieldNotesPayload(d),
              user_id: user?.id,
              user_name: user?.name,
            },
            d.org_id || organization?.id,
          );
        } catch (qe) {
          console.warn('[SyncQueue] Could not queue draft', d.id, qe);
        }
      }
      if (isOnline) {
        await syncOfflineActions();
        // Clear any drafts whose queue action is now gone (success removes it)
        const remaining = await getAllActions();
        const pendingSiteVisitIds = new Set(
          remaining
            .filter(a => a.action_type === OFFLINE_ACTIONS.UPDATE_SITE_VISIT_FIELD_NOTES)
            .map(a => a.payload?.site_visit_id),
        );
        for (const d of drafts) {
          if (!pendingSiteVisitIds.has(d.site_visit_id)) {
            try { await remove(STORES.FIELD_NOTES_DRAFTS, d.id); } catch { /* ignore */ }
          }
        }
        toast.success('All drafts pushed to sync queue');
      } else {
        toast.success(`${drafts.length} draft${drafts.length === 1 ? '' : 's'} queued — will sync when you reconnect`);
      }
      await load();
      await refreshPendingCount();
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    const data = await dumpQueueDiagnostics();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contractoros-sync-queue-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Diagnostic file downloaded');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-3 sm:px-5 py-3 flex items-center gap-3" data-testid="sync-queue-header">
        <Link to="/dashboard">
          <Button variant="ghost" size="icon" className="h-9 w-9 -ml-1 text-gray-700 hover:bg-gray-100">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-base sm:text-lg font-bold leading-tight text-gray-900">Sync Queue Recovery</h1>
          <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
            {isOnline ? <Wifi className="w-3 h-3 text-emerald-600" /> : <WifiOff className="w-3 h-3 text-amber-600" />}
            {isOnline ? 'Online' : 'Offline'}
            <span className="text-gray-300">·</span>
            <span>{pending.length} pending · {failed.length} failed · {pendingPhotos.length + failedPhotos.length} photo(s) · {drafts.length} draft(s)</span>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading || busy} data-testid="sync-queue-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="max-w-3xl mx-auto p-3 sm:p-5 space-y-5">
        {/* Action bar */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3 shadow-sm">
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleSyncAll} disabled={busy || !isOnline} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="sync-queue-sync-now">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? 'animate-spin' : ''}`} />
              Sync now
            </Button>
            {failed.length > 0 && (
              <>
                <Button onClick={handleRetryAllFailed} variant="outline" disabled={busy} data-testid="sync-queue-retry-failed">
                  Retry all failed
                </Button>
                <Button onClick={handleClearFailed} variant="outline" disabled={busy} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="sync-queue-clear-failed">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Discard failed
                </Button>
              </>
            )}
            <Button onClick={handleExport} variant="outline" data-testid="sync-queue-export">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export diagnostic
            </Button>
            {actions.length > 0 && (
              <Button onClick={handleClearAll} variant="ghost" disabled={busy} className="text-red-600 hover:bg-red-50 hover:text-red-700 ml-auto" data-testid="sync-queue-nuke">
                Nuke queue
              </Button>
            )}
          </div>
          {!isOnline && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              You're offline. Reconnect (Wi-Fi or hotspot) before tapping Sync now.
            </p>
          )}
        </div>

        {/* Failed items — most critical, show first */}
        {failed.length > 0 && (
          <section data-testid="failed-section">
            <h2 className="text-sm font-bold uppercase tracking-wider text-red-700 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Failed ({failed.length})
            </h2>
            <div className="space-y-2">
              {failed.map((a) => (
                <QueueItemCard
                  key={a.id} action={a}
                  expanded={!!expanded[a.id]} onToggle={() => setExpanded(p => ({ ...p, [a.id]: !p[a.id] }))}
                  onRetry={() => handleRetryOne(a.id)}
                  onDiscard={() => handleDiscardOne(a.id)}
                  busy={busy}
                />
              ))}
            </div>
          </section>
        )}

        {/* Pending items */}
        {pending.length > 0 && (
          <section data-testid="pending-section">
            <h2 className="text-sm font-bold uppercase tracking-wider text-amber-700 mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Pending ({pending.length})
            </h2>
            <div className="space-y-2">
              {pending.map((a) => (
                <QueueItemCard
                  key={a.id} action={a}
                  expanded={!!expanded[a.id]} onToggle={() => setExpanded(p => ({ ...p, [a.id]: !p[a.id] }))}
                  onDiscard={() => handleDiscardOne(a.id)}
                  busy={busy}
                />
              ))}
            </div>
          </section>
        )}

        {/* Photo uploads (separate channel) */}
        {(pendingPhotos.length + failedPhotos.length) > 0 && (
          <section data-testid="photos-section">
            <h2 className="text-sm font-bold uppercase tracking-wider text-blue-700 mb-2 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Photo uploads ({pendingPhotos.length} pending · {failedPhotos.length} failed)
            </h2>
            <div className="space-y-2">
              {[...failedPhotos, ...pendingPhotos].map((p) => (
                <PhotoUploadCard
                  key={p.id} photo={p}
                  onRetry={p.status === 'failed' ? () => handleRetryPhoto(p.id) : undefined}
                  onDiscard={() => handleDiscardPhoto(p.id)}
                  busy={busy}
                />
              ))}
            </div>
          </section>
        )}

        {/* Drafts */}
        {drafts.length > 0 && (
          <section data-testid="drafts-section">
            <div className="flex items-center justify-between mb-2 gap-2">
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-600 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Field Notes Drafts ({drafts.length})
              </h2>
              <Button
                size="sm"
                onClick={handlePushAllDrafts}
                disabled={busy}
                className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="push-all-drafts-btn"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Push all to sync queue
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              These are local drafts on this device. They haven't reached the server yet.
              Tap <strong>"Push to sync queue"</strong> on any draft (or use <strong>Push all</strong> above)
              to send it. {isOnline ? "It will sync immediately." : "It will sync the moment you reconnect."}
            </p>
            <div className="space-y-2">
              {drafts.map((d) => (
                <DraftCard
                  key={d.id}
                  draft={d}
                  busy={busy}
                  onPush={() => handlePushDraftToQueue(d)}
                  onDiscard={() => handleDiscardDraft(d.id)}
                />
              ))}
            </div>
          </section>
        )}

        {!loading && actions.length === 0 && drafts.length === 0 && photos.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center" data-testid="sync-queue-empty">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="font-semibold text-gray-900">All clear</p>
            <p className="text-sm text-gray-500 mt-1">No pending items, no failed items, no local drafts. Everything is in sync.</p>
          </div>
        )}

        {/* Diagnostic Logs (collapsible) */}
        <section className="mt-2" data-testid="sync-logs-section">
          <button
            onClick={() => setShowLogs(s => !s)}
            className="w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700 py-2"
            data-testid="toggle-logs"
          >
            <span>Recent sync logs ({logs.length})</span>
            <span>{showLogs ? '▾' : '▸'}</span>
          </button>
          {showLogs && (
            <div className="bg-gray-900 text-gray-100 rounded-xl p-3 font-mono text-[10px] max-h-72 overflow-y-auto">
              {logs.length === 0 ? (
                <p className="text-gray-500">No logs yet. Trigger a sync to see entries.</p>
              ) : (
                logs.map((l) => (
                  <div key={l.id} className="py-0.5">
                    <span className="text-gray-500">{new Date(l.timestamp).toLocaleTimeString()}</span>
                    <span className={
                      l.level === 'error' ? ' text-red-400 ml-1' :
                      l.level === 'warn' ? ' text-amber-300 ml-1' :
                      ' text-gray-400 ml-1'
                    }>[{l.level}]</span>
                    <span className="text-gray-100 ml-1">{l.event}</span>
                    {l.data && Object.keys(l.data).length > 0 && (
                      <span className="text-gray-400 ml-1">{JSON.stringify(l.data)}</span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function PhotoUploadCard({ photo, onRetry, onDiscard, busy }) {
  const cls = errorClassLabels[photo.last_error_class] || errorClassLabels.unknown;
  const sizeKb = Math.round((photo.data_url?.length || 0) / 1024);
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm" data-testid={`photo-upload-${photo.id}`}>
      <div className="flex items-start gap-3">
        <img
          src={photo.data_url}
          alt={photo.name}
          className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-gray-100"
          loading="lazy"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm text-gray-900 truncate">{photo.name}</p>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${photo.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
              {photo.status === 'failed' ? 'Failed' : 'Pending'}
            </span>
            {photo.last_error_class && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cls.color}`}>{cls.label}</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {sizeKb}KB · {relativeTime(photo.created_at)}
            {photo.retries > 0 && ` · ${photo.retries} retr${photo.retries === 1 ? 'y' : 'ies'}`}
          </p>
          {photo.last_error && (
            <p className="text-xs text-red-600 mt-1 break-words">{photo.last_error}</p>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
        {onRetry && (
          <Button size="sm" variant="outline" onClick={onRetry} disabled={busy} className="h-7 text-xs">
            <RefreshCw className="w-3 h-3 mr-1" /> Retry
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onDiscard} disabled={busy} className="h-7 text-xs text-red-600 hover:bg-red-50 hover:text-red-700">
          <Trash2 className="w-3 h-3 mr-1" /> Discard
        </Button>
      </div>
    </div>
  );
}

function QueueItemCard({ action, expanded, onToggle, onRetry, onDiscard, busy }) {
  const cls = errorClassLabels[action.last_error_class] || errorClassLabels.unknown;
  const label = actionTypeLabels[action.action_type] || action.action_type;
  const hasPhotos = action.payload?.field_notes?.photos?.length > 0;
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm" data-testid={`queue-item-${action.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${action.status === 'failed' ? 'bg-red-50' : 'bg-amber-50'}`}>
          {hasPhotos ? <ImageIcon className={`w-4 h-4 ${action.status === 'failed' ? 'text-red-600' : 'text-amber-600'}`} /> :
                       <FileText className={`w-4 h-4 ${action.status === 'failed' ? 'text-red-600' : 'text-amber-600'}`} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm text-gray-900">{label}</p>
            {action.last_error_class && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cls.color}`}>{cls.label}</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Created {relativeTime(action.created_at)}
            {action.retries > 0 && ` · ${action.retries} retr${action.retries === 1 ? 'y' : 'ies'}`}
            {action.next_attempt_at && new Date(action.next_attempt_at).getTime() > Date.now() &&
              ` · next attempt in ${relativeTime(action.next_attempt_at).replace(' ago', '').replace('-', '')}`}
          </p>
          {action.last_error && (
            <p className="text-xs text-red-600 mt-1 break-words">{action.last_error}</p>
          )}
          {expanded && (
            <pre className="text-[10px] text-gray-500 bg-gray-50 rounded p-2 mt-2 overflow-x-auto max-h-40">
              {JSON.stringify(previewSafe(action.payload), null, 2)}
            </pre>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <button onClick={onToggle} className="text-[11px] text-blue-600 hover:underline">
            {expanded ? 'Hide' : 'Inspect'}
          </button>
        </div>
      </div>
      <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100">
        {onRetry && (
          <Button size="sm" variant="outline" onClick={onRetry} disabled={busy} className="h-7 text-xs" data-testid={`retry-${action.id}`}>
            <RefreshCw className="w-3 h-3 mr-1" /> Retry
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onDiscard} disabled={busy} className="h-7 text-xs text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`discard-${action.id}`}>
          <Trash2 className="w-3 h-3 mr-1" /> Discard
        </Button>
      </div>
    </div>
  );
}

function DraftCard({ draft, busy, onPush, onDiscard }) {
  const notes = draft.notes || {};
  const previewText = notes.internalNotes || notes.clientNotes || '(no text)';
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm" data-testid={`draft-${draft.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
          <FileText className="w-4 h-4 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900 truncate">
            Site visit: {draft.site_visit_id}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Updated {relativeTime(draft.updated_at)}
            {notes.photos?.length ? ` · ${notes.photos.length} photo(s)` : ''}
          </p>
          <p className="text-xs text-gray-700 mt-1 line-clamp-2">{String(previewText).slice(0, 200)}</p>
          <div className="flex items-center gap-2 mt-2">
            <Button
              size="sm"
              onClick={onPush}
              disabled={busy}
              className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
              data-testid={`push-draft-${draft.id}`}
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Push to sync queue
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onDiscard}
              disabled={busy}
              className="h-7 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid={`discard-draft-${draft.id}`}
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Discard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Convert a Field Notes draft (camelCase UI shape) into the snake_case
 * `field_notes` payload expected by the offline queue's
 * UPDATE_SITE_VISIT_FIELD_NOTES processor.
 *
 * Must stay in sync with FieldNotesPage.handleSave's payload builder — same
 * keys, same defaults. Otherwise sync from this page would silently drop
 * fields that handleSave correctly serializes.
 */
function draftToFieldNotesPayload(draft) {
  const n = draft.notes || {};
  return {
    internal_notes: n.internalNotes || '',
    project_notes: n.clientNotes || '',
    client_expectations: n.clientExpectations || null,
    structured_measurements: n.measurements || [],
    photos: n.photos || [],
    sketches: n.sketches || [],
    site_conditions: n.siteConditions || [],
    custom_conditions: n.customConditions || [],
    checklist: n.checklist || [],
    is_checklist_complete: !!n.isChecklistComplete,
    internal_voice_recordings: n.internalVoiceRecordings || [],
    client_voice_recordings: n.clientVoiceRecordings || [],
  };
}

function previewSafe(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const out = {};
  for (const [k, v] of Object.entries(payload)) {
    if (Array.isArray(v) && k.toLowerCase().includes('photo')) {
      out[k] = `[${v.length} photo(s)]`;
    } else if (typeof v === 'string' && v.startsWith('data:')) {
      out[k] = '[base64 blob]';
    } else if (typeof v === 'object' && v !== null) {
      out[k] = previewSafe(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}
